import { useTenant } from '@emerald-works-nova/auth'
import {
  ExploreOutlined,
  HeadsetOutlined,
  ImageOutlined,
  ImportContactsOutlined,
  MoreHorizOutlined,
  PsychologyOutlined,
  TimerOutlined,
  VideocamOutlined
} from '@mui/icons-material'
import { Box, Dialog, Slide, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import consts from '../../consts'
import { categorySlice } from '../../reducers'
import { NavigationButtons, PageHeader, PersonalizationSkippedModal } from './components'
import {
  CurrentRolePage,
  GoalsPage,
  IntroPage,
  LearningPreferencesPage,
  TimeAvailablePage,
  TopicsPage
} from './pages'
import { useStyles } from './style'

const transitionTimeout = 300

const STEPS = {
  INTRO: 0,
  GOALS: 1,
  TOPICS: 2,
  LEARNING_PREFERENCES: 3,
  TIME_AVAILABLE: 4,
  CURRENT_ROLE: 5
}

const Transition = React.forwardRef((props, ref) => (
  <Slide direction='up' ref={ref} {...props} timeout={transitionTimeout} />
))

Transition.displayName = 'Transition'

const LPQ = ({ profile, onClose, onCloseWithoutSave, lpqParam, onFinish, mandatory = true, showSkipButton = true, showCloseButton = true, onSavePartial }) => {
  const classes = useStyles()

  // Determine if we should show intro page
  const hasCompletedOldLPQ = profile?.onboarded === true
  const hasCompletedNewLPQ = profile?.lpqCompleted === true
  const shouldShowYouAreAllSet = hasCompletedNewLPQ && lpqParam === 'lpqParam'
  const shouldShowIntro = hasCompletedOldLPQ || shouldShowYouAreAllSet

  const [activeStep, setActiveStep] = useState(shouldShowIntro ? STEPS.INTRO : STEPS.GOALS)
  const [searchTerm, setSearchTerm] = useState('')
  const [showSkipModal, setShowSkipModal] = useState(false)
  const [onboardingData, setOnboardingData] = useState({
    goals: [],
    favoriteTopics: [],
    otherTopics: [],
    learningPreferences: [],
    timeAvailable: '',
    currentRole: '',
    customCurrentRole: ''
  })
  const categories = useSelector(
    categorySlice.selectors.selectCategoryDefaultHierarchy
  )

  useEffect(() => {
    if (profile) {
      const initialData = {
        goals: profile.goals || [],
        favoriteTopics: Array.isArray(profile.favoriteTopics) ? profile.favoriteTopics : [],
        otherTopics: Array.isArray(profile.otherTopics) ? profile.otherTopics : [],
        learningPreferences: profile.learningPreferences || [],
        timeAvailable: profile.timeAvailable || '',
        currentRole: profile.currentRole || '',
        customCurrentRole: profile.customCurrentRole || ''
      }
      setOnboardingData(initialData)
    }
  }, [profile])

  const tenant = useTenant()
  const isClub = tenant?.pk === consts.DEFAULT_CLUB_PK
  const introBanner = `Hi ${profile?.name?.trim() || 'there'},\nHelp us personalise your experience by answering a few quick questions about you and how you like to learn.`
  const onboardingConfig = useMemo(() => {
    if (isClub) {
      return {
        page1: {
          title: 'What is your primary goal for using Mindtools?',
          subtitle: '',
          goals: [
            'Solve leadership or team challenges',
            'Upskill and grow into a leadership role',
            'Find content to adapt for teaching or coaching',
            'Support assignments or coursework',
            'Something else'
          ]
        },
        page2: {
          title: 'What skills or topics are you most interested in right now?',
          subtitle: 'Select all that apply so we can recommend the best content for you.'
        },
        page4: {
          title: 'On average, how much time do you usually have for learning each week?',
          timeOptions: [
            'Around 5 minutes',
            'Around 10 minutes',
            'Around 20 minutes',
            '30 minutes or more',
            "It varies / I'm flexible"
          ]
        },
        page5: {
          title: 'Which of these best describes you right now?',
          roles: [
            'Director, CEO',
            'Team Leader, Manager',
            'Early-career professional aiming for leadership',
            'Coach, Learning Designer, L&D Professional',
            'Student, Trainee, Apprentice',
            'Other / not sure'
          ]
        }
      }
    } else {
      return {
        page1: {
          title: 'What is your main goal right now?',
          subtitle: '',
          goals: [
            'Prepare for a leadership role',
            'Build confidence in a new role',
            'Develop in my current position',
            'Work toward a promotion',
            'Explore new skills and ideas',
            'Other'
          ]
        },
        page2: {
          title: 'What skills would you like to develop?',
          subtitle: 'Tell us a bit about yourself so we can help you get the most out of Mindtools.'
        },
        page4: {
          title: 'How much time do you usually have for learning?',
          timeOptions: [
            'Around 5 minutes',
            'Around 10 minutes',
            'Around 20 minutes',
            '30 minutes or more',
            "It varies / I'm flexible"
          ]
        },
        page5: {
          title: 'Which of these best describes your current role?',
          roles: [
            "I'm not in a leadership role yet",
            'I sometimes guide or support others',
            'I lead a team or project',
            'I manage other managers',
            "I'm part of a senior leadership team",
            'Other / not sure'
          ]
        }
      }
    }
  }, [isClub])

  const groupedOptions = useMemo(() => {
    return (categories || [])
      .filter(parent => parent?.children && !parent.hideFromLPQ)
      .map(parent => ({
        parentSk: parent.sk,
        parentName: parent.name,
        children: parent.children
          .filter(child => !child.hideFromLPQ)
          .map(child => ({
            sk: child.sk,
            name: child.name,
            parentName: parent.name
          }))
      }))
      .filter(group => group.children.length > 0)
  }, [categories])

  const allAvailableOptions = useMemo(() => {
    return groupedOptions.map(group => group.children).flat()
  }, [groupedOptions])

  const LEARNING_PREFERENCES = [
    { text: 'Reading articles or tips', icon: ImportContactsOutlined },
    { text: 'Listening to audio or podcasts', icon: HeadsetOutlined },
    { text: 'Watching short videos', icon: VideocamOutlined },
    { text: 'Using visual summaries', icon: ImageOutlined },
    { text: 'Learning at my own pace', icon: TimerOutlined },
    { text: 'Reflecting on ideas and experiences', icon: PsychologyOutlined },
    { text: 'Exploring real-life examples', icon: ExploreOutlined },
    { text: 'Other', icon: MoreHorizOutlined }
  ]

  // Everything the questionnaire holds right now, shaped for the profile.
  const toPartialProfile = (data) => ({
    favoriteTopics: (data.favoriteTopics || []).filter(item => item !== undefined && item !== null),
    otherTopics: (data.otherTopics || []).filter(item => item !== undefined && item !== null),
    currentRole: data.currentRole || '',
    customCurrentRole: data.customCurrentRole || '',
    goals: (data.goals || []).filter(item => item !== undefined && item !== null),
    learningPreferences: (data.learningPreferences || []).filter(item => item !== undefined && item !== null),
    timeAvailable: data.timeAvailable || ''
  })

  const savePartialData = (data) => {
    if (onSavePartial) {
      onSavePartial(toPartialProfile(data))
    }
  }

  const handleNext = () => {
    if (activeStep < STEPS.CURRENT_ROLE) {
      savePartialData(onboardingData)
      setActiveStep(prev => prev + 1)
    } else {
      handleFinish()
    }
  }

  const handleBack = () => {
    const minStep = shouldShowIntro ? STEPS.INTRO : STEPS.GOALS
    if (activeStep > minStep) {
      setActiveStep(prev => prev - 1)
    }
  }

  const handleFinish = () => {
    const partialProfile = {
      favoriteTopics: (onboardingData.favoriteTopics || []).filter(item => item !== undefined && item !== null),
      otherTopics: (onboardingData.otherTopics || []).filter(item => item !== undefined && item !== null),
      currentRole: onboardingData.currentRole || '',
      customCurrentRole: onboardingData.customCurrentRole || '',
      goals: (onboardingData.goals || []).filter(item => item !== undefined && item !== null),
      learningPreferences: (onboardingData.learningPreferences || []).filter(item => item !== undefined && item !== null),
      timeAvailable: onboardingData.timeAvailable || ''
    }
    onFinish(partialProfile)
  }

  const toggleSelection = (type, value) => {
    setOnboardingData(prev => {
      const currentArray = prev[type] || []
      const isSelected = currentArray.includes(value)

      if (isSelected) {
        return {
          ...prev,
          [type]: currentArray.filter(item => item !== value)
        }
      } else {
        return {
          ...prev,
          [type]: [...currentArray, value]
        }
      }
    })
  }

  const setSingleSelection = (type, value) => {
    setOnboardingData(prev => ({
      ...prev,
      [type]: value
    }))
  }

  const handleRoleSelection = (role) => {
    if (role === 'Other / not sure' || role === 'Other') {
      // Set currentRole to the "Other" value and preserve customCurrentRole
      setOnboardingData(prev => ({
        ...prev,
        currentRole: role,
        customCurrentRole: prev.customCurrentRole || ''
      }))
    } else {
      // Clear customCurrentRole when a predefined role is selected
      setOnboardingData(prev => ({
        ...prev,
        currentRole: role,
        customCurrentRole: ''
      }))
    }
  }

  const handleCustomRoleChange = (customRole) => {
    setOnboardingData(prev => ({
      ...prev,
      customCurrentRole: customRole
    }))
  }

  const handleAddOtherTopic = (topicName) => {
    setOnboardingData(prev => ({
      ...prev,
      otherTopics: [...(prev.otherTopics || []), topicName]
    }))
  }

  const handleClearSkills = () => {
    setOnboardingData(prev => ({
      ...prev,
      favoriteTopics: []
    }))
  }

  const handleSelectExistingSkill = (skillSk) => {
    setOnboardingData(prev => ({
      ...prev,
      favoriteTopics: [...(prev.favoriteTopics || []), skillSk]
    }))
  }

  const handleRemoveOtherTopic = (topicName) => {
    setOnboardingData(prev => ({
      ...prev,
      otherTopics: (prev.otherTopics || []).filter(topic => topic !== topicName)
    }))
  }

  // Leaving the questionnaire — by the close button, the backdrop or the skip
  // modal — is one profile write that carries the answers given so far
  // together with the dismissal. It used to be two or three concurrent writes,
  // each dispatching setProfile, so whichever landed last decided what the
  // app believed: sometimes the answers, sometimes the opt-out, never both.
  const handleSkipClick = () => {
    // Nothing is written yet: the answers stay in state in case the user
    // returns to the questionnaire, and go out with the decision below.
    setShowSkipModal(true)
  }

  const handleSkipModalClose = (dontShowAgain) => {
    setShowSkipModal(false)

    const partial = {
      ...toPartialProfile(onboardingData),
      lpqClosedAt: new Date().valueOf()
    }

    if (dontShowAgain) {
      onClose('skip-button', partial)
      return
    }

    if (onSavePartial) {
      onSavePartial(partial)
    }
    onCloseWithoutSave()
  }

  const handleReturnToQuestionnaire = () => {
    setShowSkipModal(false)
  }

  const handleCloseWithSavePartial = (from) => {
    onClose(from, toPartialProfile(onboardingData))
  }

  const isCurrentStepValid = () => {
    switch (activeStep) {
      case STEPS.INTRO:
        return true // Intro page is always valid
      case STEPS.GOALS:
        return onboardingData.goals && onboardingData.goals.length > 0
      case STEPS.TOPICS:
        return (onboardingData.favoriteTopics && onboardingData.favoriteTopics.length > 0) ||
               (onboardingData.otherTopics && onboardingData.otherTopics.length > 0)
      case STEPS.LEARNING_PREFERENCES:
        return onboardingData.learningPreferences && onboardingData.learningPreferences.length > 0
      case STEPS.TIME_AVAILABLE:
        return onboardingData.timeAvailable && onboardingData.timeAvailable !== ''
      case STEPS.CURRENT_ROLE:
        // If "Other" is selected, check for customCurrentRole, otherwise check currentRole
        if (onboardingData.currentRole === 'Other / not sure' || onboardingData.currentRole === 'Other') {
          return onboardingData.customCurrentRole && onboardingData.customCurrentRole.trim() !== ''
        }
        return onboardingData.currentRole && onboardingData.currentRole !== ''
      default:
        return false
    }
  }

  const renderCurrentPage = () => {
    switch (activeStep) {
      case STEPS.INTRO:
        return (
          <IntroPage
            shouldShowYouAreAllSet={shouldShowYouAreAllSet}
            onStart={handleNext}
          />
        )
      case STEPS.GOALS:
        return (
          <GoalsPage
            introText={introBanner}
            title={onboardingConfig.page1.title}
            subtitle={onboardingConfig.page1.subtitle}
            goals={onboardingConfig.page1.goals}
            selectedGoals={onboardingData.goals}
            onToggleGoal={(goal) => toggleSelection('goals', goal)}
            questionNumber={activeStep}
            totalQuestions={Object.keys(STEPS).length - 1}
          />
        )
      case STEPS.TOPICS:
        return (
          <TopicsPage
            title={onboardingConfig.page2?.title}
            subtitle={onboardingConfig.page2?.subtitle}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            groupedOptions={groupedOptions}
            selectedSkills={onboardingData.favoriteTopics}
            onToggleSkill={(skill) => toggleSelection('favoriteTopics', skill)}
            otherTopics={onboardingData.otherTopics}
            onAddOtherTopic={handleAddOtherTopic}
            onRemoveOtherTopic={handleRemoveOtherTopic}
            allAvailableOptions={allAvailableOptions}
            onSelectExistingSkill={handleSelectExistingSkill}
            onClearSkills={handleClearSkills}
          />
        )
      case STEPS.LEARNING_PREFERENCES:
        return (
          <LearningPreferencesPage
            preferences={LEARNING_PREFERENCES}
            selectedPreferences={onboardingData.learningPreferences}
            onTogglePreference={(preference) => toggleSelection('learningPreferences', preference)}
          />
        )
      case STEPS.TIME_AVAILABLE:
        return (
          <TimeAvailablePage
            title={onboardingConfig.page4?.title}
            timeOptions={onboardingConfig.page4?.timeOptions}
            selectedTime={onboardingData.timeAvailable}
            onSelectTime={(time) => setSingleSelection('timeAvailable', time)}
          />
        )
      case STEPS.CURRENT_ROLE:
        return (
          <CurrentRolePage
            title={onboardingConfig.page5.title}
            roles={onboardingConfig.page5.roles}
            selectedRole={onboardingData.currentRole}
            customCurrentRole={onboardingData.customCurrentRole}
            onSelectRole={handleRoleSelection}
            onCustomRoleChange={handleCustomRoleChange}
          />
        )
      default:
        return null
    }
  }

  return (
    <Dialog
      open
      onClose={(e, reason) => {
        if (mandatory && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return
        // Same exit as the close button: the answers so far are kept.
        handleCloseWithSavePartial('backdrop-click')
      }}
      disableEscapeKeyDown={mandatory}
      TransitionComponent={Transition}
      fullScreen
      className={classes.dialog}
      data-test='lpq-dialog'
      aria-labelledby='lpq-dialog-title'
      aria-describedby='lpq-dialog-description'
    >
      <Box className={classes.root}>
        <Box className={classes.container}>
          <PageHeader
            title='Welcome to Mindtools'
            questionNumber={activeStep}
            totalQuestions={Object.keys(STEPS).length - 1}
            onClose={onClose}
            onCloseWithSavePartial={handleCloseWithSavePartial}
            onSkip={handleSkipClick}
            showSkipButton={showSkipButton}
            showCloseButton={showCloseButton}
            tenant={tenant}
            showLogo={activeStep === STEPS.INTRO || !shouldShowIntro}
            hideProgress={activeStep === STEPS.INTRO}
          />

          <Box className={`${classes.contentWrapper} ${showSkipButton ? classes.contentWrapperWithSkip : classes.contentWrapperWithoutSkip}`}>
            {/* Inline (in-flow) question counter that scrolls with the content and
                sits directly above the step heading. GOALS renders its own inline
                counter (above its intro banner), so it is excluded here. */}
            {activeStep !== STEPS.INTRO && activeStep !== STEPS.GOALS && (
              <Typography
                className={classes.inlineQuestionCounter}
                data-test='lpq-question-counter'
              >
                {`Question ${activeStep} of ${Object.keys(STEPS).length - 1}`}
              </Typography>
            )}
            {renderCurrentPage()}
          </Box>

          {activeStep !== STEPS.INTRO && (
            <NavigationButtons
              activeStep={shouldShowIntro ? activeStep - 1 : activeStep}
              totalSteps={shouldShowIntro ? Object.keys(STEPS).length - 1 : Object.keys(STEPS).length}
              onBack={handleBack}
              onNext={handleNext}
              isNextDisabled={!isCurrentStepValid()}
              showSkipButton={showSkipButton}
            />
          )}

          {showSkipButton && (
            <Box className={classes.skipButtonContainer}>
              <Typography
                component='button'
                className={classes.skipButtonMobile}
                onClick={handleSkipClick}
                tabIndex={0}
                role='button'
                aria-label='Skip questionnaire for now'
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleSkipClick()
                  }
                }}
                data-test='lpq-skip-button'
              >
                Skip for now
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <PersonalizationSkippedModal
        open={showSkipModal}
        onClose={handleSkipModalClose}
        onReturnToQuestionnaire={handleReturnToQuestionnaire}
      />
    </Dialog>
  )
}

export default LPQ
